import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import FunctionalButton from "components/layout/FunctionalButton";
import StreamView from "components/streaming/StreamView";
import { useWebRTC } from "providers/WebRTCProvider";
import { useARC } from "providers/ARC/ARCContext";
import { useGimbalControl } from "providers/GimbalControlProvider";
import { useFeatures } from "providers/FeaturesProvider";
import { isGimbalCameraStream, isThermalCameraStream } from "lib/utils/connection";

const LIST_CAMERAS_SERVICE = "/camera/list";
const LIST_CAMERAS_SERVICE_TYPE = "video_pipeline_interfaces/srv/ListCameras";

/** Cameras on the gimbal head — TakePhoto should store/apply PTZ for these. */
const isGimbalMountedCamera = (name: string): boolean =>
    isGimbalCameraStream(name) || isThermalCameraStream(name);

interface TakePhotoDialogProps {
    visible: boolean;
    onConfirm: (args: { camera_name: string; ptz_yaw: number; ptz_pitch: number }) => void;
    onClose: () => void;
}

const TakePhotoDialog: React.FC<TakePhotoDialogProps> = ({ visible, onConfirm, onClose }) => {
    const { t } = useTranslation(["missions", "common"]);
    const [cameraNames, setCameraNames] = useState<string[]>([]);
    const [selectedCamera, setSelectedCamera] = useState<string>("");
    const [freeText, setFreeText] = useState("");

    const {
        activeStream,
        setActiveStream,
        availableStreams,
    } = useWebRTC();
    const { callService } = useARC();
    const { hasGimbalControl } = useFeatures();
    const {
        controlGimbal,
        setControlGimbal,
        setPhotoCaptureActive,
        state: gimbalAngle,
        sendGimbalAngle,
    } = useGimbalControl();

    const prevStreamRef = useRef<string | null>(null);
    const didChangeStreamRef = useRef(false);
    const prevControlGimbalRef = useRef<boolean>(false);

    // Match the camera service's stable camera key to a WebRTC catalog producer.
    // Case-fold only for legacy names that differ in capitalization. Do not require
    // a live status: inactive producers stay STREAM_NONE until setActiveStream starts
    // a consumer (so gating on status blocked gimbal/thermal while front was active).
    const resolveCameraStream = (name: string): string | null => {
        if (!name) return null;
        const keys = availableStreams as string[];
        return (
            keys.find((k) => k === name) ??
            keys.find((k) => k.toLowerCase() === name.toLowerCase()) ??
            null
        );
    };

    // Switch preview when the camera maps to a known producer; otherwise leave the
    // current stream so a missing name does not blank the panel.
    const previewCamera = (name: string) => {
        const key = resolveCameraStream(name);
        if (key && key !== activeStream) {
            setActiveStream(key);
            didChangeStreamRef.current = true;
        }
    };

    // Yaw/pitch inputs double as the live readout: they track the live gimbal angle while
    // unfocused, accept typed values, and command the gimbal on blur.
    const [yawText, setYawText] = useState("0.0");
    const [pitchText, setPitchText] = useState("0.0");
    const yawFocusedRef = useRef(false);
    const pitchFocusedRef = useRef(false);

    // Auto-enable gimbal control while the dialog is open — same effect as pressing the
    // bottom-bar "control gimbal" button. Keyed on hasGimbalControl too, so it still fires
    // if the gimbal becomes available (health topic lands) after the dialog is already open.
    useEffect(() => {
        if (!visible || !hasGimbalControl) return;
        prevControlGimbalRef.current = controlGimbal;
        setControlGimbal(true);
        setPhotoCaptureActive(true);
        return () => {
            setControlGimbal(prevControlGimbalRef.current);
            setPhotoCaptureActive(false);
        };
        // controlGimbal intentionally excluded — including it would re-run on our own set.
    }, [visible, hasGimbalControl]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!visible) return;

        prevStreamRef.current = activeStream;

        callService(LIST_CAMERAS_SERVICE, {}, { serviceType: LIST_CAMERAS_SERVICE_TYPE })
            .then((res: any) => {
                const names: string[] = res?.camera_names ?? [];
                setCameraNames(names);
                if (names.length > 0) {
                    setSelectedCamera(names[0]);
                    previewCamera(names[0]);
                }
            })
            .catch(() => {
                setCameraNames([]);
            });
    }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep the input fields in sync with the live gimbal angle, but never clobber a field
    // the user is currently editing.
    useEffect(() => {
        if (!hasGimbalControl) return;
        if (!yawFocusedRef.current) setYawText(Number(gimbalAngle?.yaw ?? 0).toFixed(1));
        if (!pitchFocusedRef.current) setPitchText(Number(gimbalAngle?.pitch ?? 0).toFixed(1));
    }, [hasGimbalControl, gimbalAngle?.yaw, gimbalAngle?.pitch]);

    const commitAngle = () => {
        const yaw = parseFloat(yawText);
        const pitch = parseFloat(pitchText);
        if (Number.isFinite(yaw) && Number.isFinite(pitch)) {
            sendGimbalAngle({ yaw, pitch });
        }
    };

    const handleSelectCamera = (name: string) => {
        setSelectedCamera(name);
        previewCamera(name);
    };

    const handleConfirm = () => {
        const camera_name = cameraNames.length === 0 ? freeText.trim() : selectedCamera;
        if (!camera_name) return;
        const storePtz = hasGimbalControl && isGimbalMountedCamera(camera_name);
        onConfirm({
            camera_name,
            ptz_yaw: storePtz ? Number(gimbalAngle?.yaw ?? 0) : 0.0,
            ptz_pitch: storePtz ? Number(gimbalAngle?.pitch ?? 0) : 0.0,
        });
        cleanup();
    };

    const handleClose = () => {
        cleanup();
        onClose();
    };

    // Restores the preview stream. Gimbal control state is restored by the activation
    // effect's cleanup when the dialog unmounts (visible → false).
    const cleanup = () => {
        if (didChangeStreamRef.current && prevStreamRef.current !== null) {
            setActiveStream(prevStreamRef.current);
        }
        didChangeStreamRef.current = false;
    };

    const canConfirm = cameraNames.length > 0 ? !!selectedCamera : freeText.trim().length > 0;

    return (
        <AnimatedModal
            visible={visible}
            onClose={handleClose}
            className="flex h-[60vh] w-[60vw] max-w-[60vw] gap-4 rounded-r10 bg-black100 p-4"
            zIndexClassName="z-[100]"
        >
            {/* Left: live preview */}
            <div className="flex-1 overflow-hidden rounded-r5 bg-black90">
                <StreamView className="h-full w-full" />
            </div>

            {/* Right: camera picker + PTZ + actions */}
            <div className="flex flex-col justify-between" style={{ minWidth: 220 }}>
                <div className="flex flex-col gap-2">
                    <p className="text-h5 mb-1 font-semibold">{t("dialogs.camera")}</p>
                    {cameraNames.length > 0 ? (
                        cameraNames.map((name) => (
                            <FunctionalButton
                                key={name}
                                title={name}
                                className={`text-h5 rounded-r5 px-3 py-1 ${
                                    selectedCamera === name
                                        ? "bg-primary2"
                                        : "bg-black80 hover:bg-black70"
                                }`}
                                onClick={() => handleSelectCamera(name)}
                            />
                        ))
                    ) : (
                        <div className="flex flex-col gap-1">
                            <p className="text-h5 text-black40">{t("dialogs.noPreview")}</p>
                            <input
                                type="text"
                                value={freeText}
                                onChange={(e) => setFreeText(e.target.value)}
                                placeholder={t("dialogs.cameraNamePlaceholder")}
                                className="text-h5 rounded-r5 border border-black60 bg-black70 px-2 py-1 focus:border-primary2 focus:outline-none"
                            />
                        </div>
                    )}

                    {hasGimbalControl && (
                        <div className="mt-3 flex flex-col gap-2">
                            <p className="text-h5 font-semibold">{t("dialogs.ptz")}</p>
                            <label className="text-h5 flex items-center justify-between gap-2 text-black40">
                                {t("dialogs.yaw")}
                                <input
                                    type="number"
                                    value={yawText}
                                    onChange={(e) => setYawText(e.target.value)}
                                    onFocus={() => (yawFocusedRef.current = true)}
                                    onBlur={() => {
                                        yawFocusedRef.current = false;
                                        commitAngle();
                                    }}
                                    className="text-h5 w-20 rounded-r5 border border-black60 bg-black70 px-2 py-1 text-right focus:border-primary2 focus:outline-none"
                                />
                            </label>
                            <label className="text-h5 flex items-center justify-between gap-2 text-black40">
                                {t("dialogs.pitch")}
                                <input
                                    type="number"
                                    value={pitchText}
                                    onChange={(e) => setPitchText(e.target.value)}
                                    onFocus={() => (pitchFocusedRef.current = true)}
                                    onBlur={() => {
                                        pitchFocusedRef.current = false;
                                        commitAngle();
                                    }}
                                    className="text-h5 w-20 rounded-r5 border border-black60 bg-black70 px-2 py-1 text-right focus:border-primary2 focus:outline-none"
                                />
                            </label>
                        </div>
                    )}
                </div>

                <div className="mt-4 flex flex-col gap-2">
                    <FunctionalButton
                        title={t("common:cancel")}
                        titleClassName="text-black60"
                        className="cancel-button"
                        onClick={handleClose}
                    />
                    <FunctionalButton
                        title={t("common:ok")}
                        className="h-7 w-full rounded-r5 bg-primary2"
                        onClick={handleConfirm}
                        disabled={!canConfirm}
                    />
                </div>
            </div>
        </AnimatedModal>
    );
};

export default TakePhotoDialog;
